import { createSlice, PayloadAction } from '@reduxjs/toolkit'

type Screen = "uploadCV" | "personalDetails" | "academicDetails" | "preferenceDetails" | ""

interface OnboardState {
    mode: string;
    currentScreen: Screen;
    disableNxtBtn: boolean;
    nameDirtyFlag: boolean,
    purseDirtyFlag: boolean,
    residenceDirtyFlag: boolean,
    gpaDirtyFlag: boolean,
    workExpDirtyFlag: boolean,
    researchExpDirtyFlag: boolean,
    stdTestDirtyFlag: boolean,
    langTestDirtyFlag: boolean,
    disciplineDirtyFlag: boolean,
    prioritiesDirtyFlag: boolean,
    tuitionFeeDirtyFlag: boolean,
    file: File | null,
    fileName: string,
    progress: number,
    fileUploaded: boolean,
    studentFirstName: string,
    studentLastName: string,
    studentResidenceCountry: string,
    studentZipCode: string,
    studentPurse: string,
    yearOfPursue: string,
    gpaValue: { unweighted: string, weighted: string, underGraduate: string, graduate: string },
    experience: { years: string, months: string, researchYears: string, researchMonths: string },
    selectedTests: { test: string; score: string }[];
    selectedLangTests: { test: string; score: string }[];
    discipline: string | null,
    priorities: any[],
    tuitionFee: any[],
    inputError: { test: string, error: boolean },
}

const initialState: OnboardState = {
    mode: '',
    currentScreen: "",
    disableNxtBtn: false,
    nameDirtyFlag: false,
    purseDirtyFlag: false,
    residenceDirtyFlag: false,
    gpaDirtyFlag: false,
    workExpDirtyFlag: false,
    researchExpDirtyFlag: false,
    stdTestDirtyFlag: false,
    langTestDirtyFlag: false,
    disciplineDirtyFlag: false,
    prioritiesDirtyFlag: false,
    tuitionFeeDirtyFlag: false,
    file: null,
    fileName: '',
    progress: 0,
    fileUploaded: false,
    studentFirstName: '',
    studentLastName: '',
    studentResidenceCountry: '',
    studentZipCode: '',
    studentPurse: 'master',
    yearOfPursue: '',
    gpaValue: {
        unweighted: '',
        weighted: '',
        underGraduate: '',
        graduate: ''
    },
    experience: {
        years: '', months: '', researchYears: '', researchMonths: ''
    },
    selectedTests: [{ test: "SAT", score: "" }],
    selectedLangTests: [{ test: "TOEFL", score: "" }],
    discipline: null,
    priorities: [],
    tuitionFee: [],
    inputError: { test: '', error: false },
}

const onboardSlice = createSlice({
    name: 'onboardData',
    initialState,
    reducers: {
        modeChange: (state, action: PayloadAction<string>) => {
            state.mode = action.payload
        },
        currentScreenChange: (state, action: PayloadAction<Screen>) => {
            state.currentScreen = action.payload
        },
        disableNxtBtnChange: (state, action: PayloadAction<boolean>) => {
            state.disableNxtBtn = action.payload
        },
        setDisableNxtBtn: (state, action: PayloadAction<boolean>) => {
            state.disableNxtBtn = action.payload;
        },
        setNameDirtyFlag: (state, action: PayloadAction<boolean>) => {
            state.nameDirtyFlag = action.payload;
        },
        setPurseDirtyFlag: (state, action: PayloadAction<boolean>) => {
            state.purseDirtyFlag = action.payload;
        },
        setResidenceDirtyFlag: (state, action: PayloadAction<boolean>) => {
            state.residenceDirtyFlag = action.payload;
        },
        onboardBooleanChange: <K extends keyof OnboardState>(
            state: OnboardState,
            action: PayloadAction<{ key: K; value: OnboardState[K] }> // Ensure value type matches the key
        ) => {
            state[action.payload.key] = action.payload.value;
        },
        setStudentFirstName: (state, action: PayloadAction<string>) => {
            state.studentFirstName = action.payload;
        },
        setStudentLastName: (state, action: PayloadAction<string>) => {
            state.studentLastName = action.payload;
        },
        setStudentResidenceCountry: (state, action: PayloadAction<string>) =>{
            state.studentResidenceCountry = action.payload;
        },
        setStudentZipCode: (state, action: PayloadAction<string>) =>{
            state.studentZipCode = action.payload;
        },
        setStudentPurse: (state, action: PayloadAction<string>) => {
            state.studentPurse = action.payload;
        },
        setYearOfPursue: (state, action: PayloadAction<string>) => {
            state.yearOfPursue = action.payload;
        },
        setFile: (state, action: PayloadAction<File | null>) => {
            state.file = action.payload;
        },
        setFileName: (state, action: PayloadAction<string>) => {
            state.fileName = action.payload;
        },
        setProgress: (state, action: PayloadAction<number>) => {
            state.progress = action.payload;
        },
        setFileUploaded: (state, action: PayloadAction<boolean>) => {
            state.fileUploaded = action.payload;
        },
        setGpaValue: (state, action: PayloadAction<Partial<OnboardState["gpaValue"]>>) => {
            state.gpaValue = { ...state.gpaValue, ...action.payload }
        },
        setExperience: (state, action: PayloadAction<Partial<OnboardState["experience"]>>) => {
            state.experience = { ...state.experience, ...action.payload }
        },
        setSelectedTests: (state, action: PayloadAction<OnboardState["selectedTests"]>) => {
            state.selectedTests = action.payload;
        },
        setSelectedLangTests: (state, action: PayloadAction<OnboardState["selectedLangTests"]>) => {
            state.selectedLangTests = action.payload;
        },
        setDiscipline: (state, action: PayloadAction<string | null>) => {
            state.discipline = action.payload;
        },
        setPriorities: (state, action: PayloadAction<any[]>) => {
            state.priorities = action.payload;
        },
        setTuitionFee: (state, action: PayloadAction<any[]>) => {
            state.tuitionFee = action.payload;
        },
        setInputError: (state, action: PayloadAction<OnboardState["inputError"]>) => {
            state.inputError = action.payload;
        },
        resetOnboardStoreState: () => initialState,
    }
})

export const {
    modeChange,
    currentScreenChange,
    disableNxtBtnChange,
    setStudentFirstName,
    setStudentLastName,
    setStudentResidenceCountry,
    setStudentZipCode,
    setStudentPurse,
    setYearOfPursue,
    setFile,
    setFileName,
    setProgress,
    setFileUploaded,
    setGpaValue,
    setExperience,
    setSelectedTests,
    setSelectedLangTests,
    setDiscipline,
    setPriorities,
    setTuitionFee,
    setInputError,
    setNameDirtyFlag,
    setPurseDirtyFlag,
    setResidenceDirtyFlag,
    resetOnboardStoreState,
    onboardBooleanChange,
} = onboardSlice.actions
export default onboardSlice.reducer